(function () {
  // Variantify (shopify://apps/variantify) splits each product's grid tile into
  // one clone per colorway (li.variantify-clone, tagged with
  // data-variantify-handle / data-variantify-color) and always inserts those
  // clones right next to each other, immediately after the original (hidden)
  // product tile. That grouping is baked into Variantify's own bundled script
  // (variantify_split.js), which we don't control or ship. Variantify also
  // waits a couple of seconds after the page has painted before it touches
  // the DOM at all (it looks like it's fetching variant data first) - that
  // dead period, followed by the sudden swap, is what causes the flash of
  // the original unsplit grid.
  //
  // IMPORTANT: an earlier version of this script marked a grid "ready" (and
  // revealed it) the first time 300ms passed with no DOM changes - but during
  // Variantify's dead period BEFORE it starts, there are no changes either,
  // so that fired immediately and revealed the wrong, unsplit grid straight
  // away. This version fixes that: it will not reveal a grid until it has
  // actually seen Variantify's own markers appear in it (data-variantify /
  // variantify-clone / variantify-processed), and only THEN waits for things
  // to go quiet before revealing. A fallback timeout still forces a reveal
  // regardless, so a grid is never left hidden forever if Variantify is slow,
  // errors out, or simply isn't active on a given page.
  //
  // Once revealed, it also makes the *minimum* possible change to tile order:
  // whatever sort the collection actually uses (manual drag order,
  // best-selling, price, etc, set normally in Shopify admin) is left
  // untouched. We only step in when two tiles of the same product would
  // otherwise land back to back, swapping the second one with the nearest
  // later tile that breaks the clash without creating a new one.

  var GRID_SELECTOR = 'ul.product-grid';
  var ITEM_CLASS = 'product-grid__item';
  var VARIANTIFY_MARKER_SELECTOR = '[data-variantify], .variantify-clone, .variantify-processed';
  var DEBOUNCE_MS = 300;
  var READY_FALLBACK_MS = 6000;
  var READY_CLASS = 'variantify-ready';

  var gridState = new WeakMap();

  function getKey(li) {
    return li.getAttribute('data-variantify-handle') || li.getAttribute('data-product-id') || null;
  }

  function isVisible(li) {
    return li.style.display !== 'none' && window.getComputedStyle(li).display !== 'none';
  }

  function markReady(grid) {
    var state = gridState.get(grid);
    if (state && state.revealed) return;
    if (state) state.revealed = true;
    grid.classList.add(READY_CLASS);
  }

  function variantifyHasStarted(grid) {
    return !!grid.querySelector(VARIANTIFY_MARKER_SELECTOR);
  }

  // Minimal-change de-duplication: walk the list in its existing order and,
  // whenever an item shares a key with the one immediately before it, swap it
  // with the nearest later item that (a) doesn't share a key with what's now
  // before it and (b) won't itself end up next to a matching key after the
  // swap. Everything else in the sequence stays exactly where it was.
  function deconflict(items) {
    var arr = items.slice();

    for (var i = 1; i < arr.length; i++) {
      if (arr[i].key == null || arr[i].key !== arr[i - 1].key) continue;

      var swapped = false;
      for (var j = i + 1; j < arr.length; j++) {
        if (arr[j].key === arr[i - 1].key) continue;

        var prevOk = j - 1 === i ? true : arr[j - 1].key !== arr[i].key;
        var nextOk = j + 1 >= arr.length ? true : arr[j + 1].key !== arr[i].key;

        if (prevOk && nextOk) {
          var tmp = arr[i];
          arr[i] = arr[j];
          arr[j] = tmp;
          swapped = true;
          break;
        }
      }

      if (!swapped && i + 1 < arr.length) {
        var tmp2 = arr[i];
        arr[i] = arr[i + 1];
        arr[i + 1] = tmp2;
      }
    }

    return arr;
  }

  function reorderAndReveal(grid) {
    var state = gridState.get(grid);
    if (!state || state.revealed) return;

    // Variantify hasn't touched this grid yet (still in its pre-processing
    // dead period, or it never will on this page). Don't reveal - just keep
    // waiting. The fallback timeout is the only thing allowed to reveal a
    // grid Variantify never touches.
    if (!variantifyHasStarted(grid)) return;

    var children = Array.prototype.slice.call(grid.children);
    var visibleItems = children.filter(function (li) {
      return li.tagName === 'LI' && li.classList.contains(ITEM_CLASS) && isVisible(li);
    });

    if (visibleItems.length >= 2) {
      var items = visibleItems.map(function (li) { return { el: li, key: getKey(li) }; });
      var newOrder = deconflict(items).map(function (item) { return item.el; });

      var changed = false;
      for (var i = 0; i < newOrder.length; i++) {
        if (newOrder[i] !== visibleItems[i]) {
          changed = true;
          break;
        }
      }

      if (changed) {
        var fragment = document.createDocumentFragment();
        newOrder.forEach(function (el) {
          fragment.appendChild(el);
        });
        grid.appendChild(fragment);
      }
    }

    markReady(grid);
  }

  function scheduleCheck(grid) {
    var state = gridState.get(grid);
    if (!state || state.revealed) return;

    if (state.debounceTimer) {
      window.clearTimeout(state.debounceTimer);
    }
    state.debounceTimer = window.setTimeout(function () {
      state.debounceTimer = null;
      reorderAndReveal(grid);
    }, DEBOUNCE_MS);
  }

  function observeGrid(grid) {
    if (gridState.has(grid)) return;
    gridState.set(grid, { revealed: false, debounceTimer: null });

    var observer = new MutationObserver(function () {
      scheduleCheck(grid);
    });
    observer.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    // Safety net: never leave a grid hidden forever if Variantify is slow,
    // throws an error, or simply isn't active on this page.
    window.setTimeout(function () { markReady(grid); }, READY_FALLBACK_MS);

    // Covers the (unlikely) case where Variantify has already finished by the
    // time this script runs.
    scheduleCheck(grid);
  }

  function scanForGrids(root) {
    if (root.matches && root.matches(GRID_SELECTOR)) {
      observeGrid(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll(GRID_SELECTOR).forEach(observeGrid);
    }
  }

  function init() {
    scanForGrids(document);

    var bodyObserver = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var addedNodes = mutations[m].addedNodes;
        for (var n = 0; n < addedNodes.length; n++) {
          var node = addedNodes[n];
          if (node instanceof HTMLElement) {
            scanForGrids(node);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
